import { createCanvas } from './loadImage';
import { isCanvasMemoryScopeActive, releaseScopedCanvas } from './canvasMemory';
import type { StaticRenderCache } from './StaticRenderCache';

export type MaskChannel = 'luminance' | 'red' | 'green' | 'blue';

/** Restores a crisp-but-antialiased edge after H.264 4:2:0 has spread a
 * packed RGB screen mask into neighbouring pixels. */
export function sharpenPackedMotionMaskFactor(factor: number): number {
  const low = 40 / 255;
  const high = 215 / 255;
  if (factor <= low) return 0;
  if (factor >= high) return 1;
  const normalized = (factor - low) / (high - low);
  return normalized * normalized * (3 - 2 * normalized);
}

export function getMaskFactor(
  pixels: Uint8ClampedArray,
  index: number,
  channel: MaskChannel = 'luminance'
): number {
  const alpha = pixels[index + 3] / 255;
  if (channel === 'red') return (pixels[index] / 255) * alpha;
  if (channel === 'green') return (pixels[index + 1] / 255) * alpha;
  if (channel === 'blue') return (pixels[index + 2] / 255) * alpha;
  return ((pixels[index] + pixels[index + 1] + pixels[index + 2]) / (255 * 3)) * alpha;
}

export function applyMask(
  screenCanvas: HTMLCanvasElement,
  maskImage: CanvasImageSource,
  channel: MaskChannel = 'luminance',
  cache?: StaticRenderCache,
  sharpenPackedMotionEdge = false
): void {
  const width = screenCanvas.width;
  const height = screenCanvas.height;

  const screenCtx = screenCanvas.getContext('2d');
  if (!screenCtx) {
    throw new Error('2D context is not available for screen canvas');
  }

  if (cache) {
    const region = cache.mask(maskImage, width, height, channel);
    if (!region.width || !region.height) {
      screenCtx.clearRect(0, 0, width, height);
      return;
    }
    const screenData = screenCtx.getImageData(region.x, region.y, region.width, region.height);
    for (let index = 0; index < screenData.data.length; index += 4) {
      const rawFactor = getMaskFactor(region.data, index, channel);
      const factor = sharpenPackedMotionEdge ? sharpenPackedMotionMaskFactor(rawFactor) : rawFactor;
      screenData.data[index + 3] = Math.round(screenData.data[index + 3] * factor);
    }
    screenCtx.clearRect(0, 0, width, height);
    screenCtx.putImageData(screenData, region.x, region.y);
    return;
  }

  const maskCanvas = createCanvas(width, height);
  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) {
    throw new Error('2D context is not available for mask canvas');
  }

  maskCtx.clearRect(0, 0, width, height);
  maskCtx.drawImage(maskImage, 0, 0, width, height);

  if (isCanvasMemoryScopeActive()) {
    for (let y = 0; y < height; y += 128) {
      const rows = Math.min(128, height - y);
      const screenData = screenCtx.getImageData(0, y, width, rows);
      const maskData = maskCtx.getImageData(0, y, width, rows);
      for (let index = 0; index < screenData.data.length; index += 4) {
        const rawFactor = getMaskFactor(maskData.data, index, channel);
        const factor = sharpenPackedMotionEdge ? sharpenPackedMotionMaskFactor(rawFactor) : rawFactor;
        screenData.data[index + 3] = Math.round(screenData.data[index + 3] * factor);
      }
      screenCtx.putImageData(screenData, 0, y);
    }
    releaseScopedCanvas(maskCanvas);
    return;
  }

  const screenData = screenCtx.getImageData(0, 0, width, height);
  const maskData = maskCtx.getImageData(0, 0, width, height);

  for (let index = 0; index < screenData.data.length; index += 4) {
    const rawFactor = getMaskFactor(maskData.data, index, channel);
    const factor = sharpenPackedMotionEdge ? sharpenPackedMotionMaskFactor(rawFactor) : rawFactor;
    screenData.data[index + 3] = Math.round(screenData.data[index + 3] * factor);
  }

  screenCtx.putImageData(screenData, 0, 0);
}
